import { apiClient } from 'api/apiClient';
import type { TExcalidrawInitialData } from 'app/pages/MoodBoard/types/excalidraw';

const RESOURCE = '/moodboards';

export type TMoodBoardStatuses = 'approved' | 'notSet' | 'rejected';

// An image owned by a board (its thumbnail snapshot, or an image placed on the
// canvas), as the BE returns it: direct (public blob/CDN) URLs at several sizes
// plus the stored file's id and name. The design manager uploads these when
// the board is edited; the portal only reads them.
export interface IMoodBoardImage {
	fileName: string;
	fileId: string;
	thumbnail: string;
	small: string;
	original: string;
	large: string;
}

export interface IMoodBoard {
	id: string;
	// Direct (public blob/CDN) URL of the board's thumbnail snapshot, uploaded
	// by the design manager and rendered by the list. Null for a board that has
	// none yet.
	thumbnailUrl: string | null;
	title?: string;
	// The client's approval of the board, as the BE names it.
	approvalStatus: TMoodBoardStatuses;
}

export type TGetMoodBoardsResponse = IMoodBoard[];

const MOOD_BOARD_STATUSES: readonly TMoodBoardStatuses[] = [
	'approved',
	'notSet',
	'rejected',
];

// A board as it comes off the wire. The BE serialises `approvalStatus` from
// its enum in PascalCase (`Approved`, `NotSet`), so it is typed loosely here
// and normalised by `normaliseMoodBoardStatus` before it reaches callers.
interface IMoodBoardResponse extends Omit<IMoodBoard, 'approvalStatus'> {
	approvalStatus?: string | null;
}

// Maps the BE's casing onto the portal's camelCase union. Anything that
// isn't a known status, including a missing one, reads as not yet set, so a
// surprising value degrades to "awaiting review" rather than crashing a card.
export const normaliseMoodBoardStatus = (
	raw: string | null | undefined,
): TMoodBoardStatuses => {
	if (!raw) {
		return 'notSet';
	}

	const status = raw.charAt(0).toLowerCase() + raw.slice(1);

	return MOOD_BOARD_STATUSES.includes(status as TMoodBoardStatuses)
		? (status as TMoodBoardStatuses)
		: 'notSet';
};

const normaliseMoodBoard = (board: IMoodBoardResponse): IMoodBoard => ({
	...board,
	approvalStatus: normaliseMoodBoardStatus(board.approvalStatus),
});

export const isMoodBoardStatusApproved = (
	status: TMoodBoardStatuses,
): boolean => {
	return status === 'approved';
};

export const isMoodBoardStatusRejected = (
	status: TMoodBoardStatuses,
): boolean => {
	return status === 'rejected';
};

export const isMoodBoardStatusNotSet = (
	status: TMoodBoardStatuses,
): boolean => {
	return status === 'notSet';
};

// Every portal endpoint is addressed by the client's access token (from the
// page's `?token=` search param), so the token is part of the path, as it is
// for proposals and invoices.
export const getMoodBoards = async (
	token: string,
): Promise<TGetMoodBoardsResponse> => {
	const { data } = await apiClient.get(`${RESOURCE}/${token}`);
	const boards = (data?.data ?? []) as IMoodBoardResponse[];

	return boards.map(normaliseMoodBoard);
};

// A pinned comment as the BE stores it. `x`/`y` are Excalidraw scene
// coordinates, so pins track the board as it pans and zooms. `id` is the BE's
// identity for the comment; it is normally present on a loaded comment, but
// the contract leaves it optional, so a comment may come back without one.
export interface IMoodBoardComment {
	id?: string;
	text: string;
	x: number;
	y: number;
}

// One comment as the update endpoint accepts it: the same shape, where a
// missing `id` tells the BE to create the comment and an `id` tells it which
// existing comment to update.
export type TMoodBoardCommentPayload = IMoodBoardComment;

// Links each Excalidraw file id in a scene to the BE `fileId` of the upload
// that stores it, so on load the image's URL is resolved from the board's
// `images` rather than trusting the URL persisted in the scene.
export type TMoodBoardSceneFiles = Record<string, string>;

// The board's background image: an upload the design manager sets to fill the
// whole canvas behind the scene. `url` is the upload's link as last saved,
// used when the upload can't be matched among the board's `images` (a board
// copied from a template). `color` is the canvas colour it replaced, which the
// design manager restores when the image is removed; the portal doesn't use it.
export interface IMoodBoardBackground {
	fileId: string;
	url: string;
	color: string;
}

// The scene as the board stores it: Excalidraw's own data plus the file-id
// map and background image. Excalidraw ignores the extra keys when seeding
// from it.
export type TMoodBoardScene = TExcalidrawInitialData & {
	moodBoardFiles?: TMoodBoardSceneFiles;
	moodBoardBackground?: IMoodBoardBackground;
};

// A single board, as the detail endpoint returns it: the list fields plus the
// Excalidraw scene the canvas renders, the images placed on it and its
// comments. The list endpoint returns none of these, so they are only
// available once a board is opened.
export interface IMoodBoardDetail extends IMoodBoard {
	title: string;
	json: TMoodBoardScene | null;
	// Every image uploaded to the board. The canvas resolves each scene image's
	// URL from here on load, matched through the scene's `moodBoardFiles` map
	// (see `resolveSceneImages` in the page's utils).
	images: IMoodBoardImage[];
	comments: IMoodBoardComment[];
}

// The BE persists the scene as a serialized JSON *string* rather than a nested
// object, so `json` comes off the wire stringified.
export interface IGetMoodBoardResponse extends IMoodBoardResponse {
	title?: string;
	json?: string | TMoodBoardScene | null;
	images?: IMoodBoardImage[] | null;
	comments?: IMoodBoardComment[] | null;
}

// Parses the stored scene. A scene that isn't valid JSON yields null, which
// seeds an empty board rather than failing the query.
const parseSceneJson = (
	raw: IGetMoodBoardResponse['json'],
): TMoodBoardScene | null => {
	if (raw === null || raw === undefined || raw === '') {
		return null;
	}

	// Tolerate a BE/mock that hands back an already-parsed scene.
	if (typeof raw !== 'string') {
		return raw;
	}

	try {
		return JSON.parse(raw) as TMoodBoardScene;
	} catch {
		return null;
	}
};

export const getMoodBoard = async (
	token: string,
	id: string,
): Promise<IMoodBoardDetail> => {
	const { data } = await apiClient.get(
		`${RESOURCE}/${token}/${encodeURIComponent(id)}`,
	);
	const board = data?.data as IGetMoodBoardResponse;

	// An untitled board comes back without a `title`; normalise it to an empty
	// string so callers only have to decide what to show, not what it can be.
	// Likewise a board with no images or comments yet may omit either array.
	return {
		...board,
		approvalStatus: normaliseMoodBoardStatus(board?.approvalStatus),
		title: board?.title ?? '',
		json: parseSceneJson(board?.json),
		images: board?.images ?? [],
		comments: board?.comments ?? [],
	};
};

// Fields the update endpoint accepts; each is independent, so a call sends
// only the ones it means to change. `comments` replaces the board's whole
// comment set: every comment is sent, with the ones the BE already knows
// carrying their `id` and brand-new ones carrying none. `approvalStatus`
// records the client's approval or rejection of the board.
export interface IMoodBoardPatchPayload {
	comments?: TMoodBoardCommentPayload[];
	approvalStatus?: TMoodBoardStatuses;
}

export const patchMoodBoard = async (
	token: string,
	id: string,
	payload: IMoodBoardPatchPayload,
): Promise<unknown> => {
	const { data } = await apiClient.patch(
		`${RESOURCE}/${token}/${encodeURIComponent(id)}`,
		payload,
	);

	return data;
};
